import { PriceLabel } from '@/components/features/shared/utils'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { SelectableRow } from '@/components/ui/selectable-row'
import { PaymentStatus } from '@/domain/payments/payments'
import { AlertCircle, CheckSquare, DollarSign, X } from 'lucide-react'
import { useState } from 'react'
import { PaymentRow } from '../sub-components/PaymentRow'
import { PaymentRowInfo } from '../sub-components/PaymentRowInfo'

interface SelectPendingPaymentsModalProps {
  isOpen: boolean
  onClose: () => void
  payments: PaymentRow[]
  onPayMultiple: (paymentIds: string[]) => void
  isPending: boolean
  onSuccess?: () => void
}

export const SelectPendingPaymentsModal = ({
  isOpen,
  onClose,
  payments,
  onPayMultiple,
  isPending,
  onSuccess,
}: SelectPendingPaymentsModalProps) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const pendingPayments = payments.filter(p => p.status === PaymentStatus.PENDING || p.status === PaymentStatus.REFUNDED)

  const totalSelected = selectedIds.size
  const totalAmount = pendingPayments
    .filter(p => selectedIds.has(p.id))
    .reduce((sum, p) => sum + (p.amount || 0) + (p.interestSum || 0), 0)

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const selectAll = () => {
    setSelectedIds(prev => {
      if (prev.size === pendingPayments.length) {
        return new Set()
      }
      return new Set(pendingPayments.map(p => p.id))
    })
  }

  const handleConfirm = () => {
    if (selectedIds.size === 0) return
    onPayMultiple(Array.from(selectedIds))
    onClose()
    setSelectedIds(new Set())
    onSuccess?.()
  }

  if (pendingPayments.length === 0) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pagar múltiples</DialogTitle>
            <div className="flex items-center gap-2 text-muted-foreground text-sm py-4">
              <AlertCircle className="h-4 w-4" />
              No hay pagos pendientes para seleccionar
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cerrar</Button>
            </DialogFooter>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Pagar múltiples pagos pendientes</DialogTitle>
          <DialogDescription>
            Elige los pagos pendientes del contrato a cobrar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 my-4">
          <div className="flex items-center justify-between pb-2 border-b">
            <span className="text-sm text-muted-foreground font-medium">
              {pendingPayments.length} pago{pendingPayments.length !== 1 ? 's' : ''} pendiente{pendingPayments.length !== 1 ? 's' : ''}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={selectAll}
              disabled={isPending}
            >
              <CheckSquare className="h-3 w-3 mr-1" />
              Seleccionar todos
            </Button>
          </div>

          <div className="space-y-2 overflow-y-auto pr-2 flex-grow">
            {pendingPayments.map(p => {
              const isSelected = selectedIds.has(p.id)
              return (
                <SelectableRow
                  key={p.id}
                  checked={isSelected}
                  disabled={isPending}
                  onCheckChange={() => toggleSelection(p.id)}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={isSelected}
                      disabled={isPending}
                    />
                    <PaymentRowInfo payment={p} />
                  </div>
                  <PriceLabel price={(p.amount || 0) + (p.interestSum || 0)} />
                </SelectableRow>
              )
            })}
          </div>
        </div>

        <div className="flex items-center justify-between bg-muted/30 p-3 rounded-lg border">
          <span className="text-sm font-medium">Total a pagar:</span>
          <span className="text-lg font-bold font-mono">${totalAmount.toLocaleString('es-AR')}</span>
        </div>

        <DialogFooter className="mt-4">
          <div className='flex justify-between w-full'>
            <Button variant="outline" onClick={onClose} disabled={isPending}>
              <X className="h-3.5 w-3.5 mr-1.5" />
              Cancelar
            </Button>
            <Button
              variant="info"
              onClick={handleConfirm}
              disabled={selectedIds.size === 0 || isPending}
            >
              <DollarSign className="h-3.5 w-3.5 mr-1.5" />
              Pagar {totalSelected > 0 ? `(${totalSelected})` : ''}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
